import { Fragment, useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import { leadsAPI, bilerSearchFromParams, bilerSearchToParams } from './api'
import type { BilerMerkeFacet, BilerModellBreakdown, BilerModellFacet, BilerOrgRow, BilerSearchFilters } from './api'
import Pagination from './Pagination'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { useLatest } from '../Bruktbil/useLatest'
import { buildBilerSummary, SUMMARY_MODELL_LIMIT } from './bilerSummary'
import { useBookmarks } from './useBookmarks'
import BookmarkStar from './BookmarkStar'
import ExportButtons from '../../components/ExportButtons'
import { useKanEksportere } from './eksportTilgang'
import { BILER_EXPORT_LIMIT, bilerExportColumns } from './bilerExport'

interface Props {
  page: number
  onPageChange: (page: number) => void
  markedsomradeIds?: string
  vehicleClass?: string
}

const PAGE_SIZE = 20

/** The b* keys this tab owns on the Leads URL. Scope and vehicle class are
 *  the page's own mo/vc params, so bvc/bmo are only written on the "Se" link. */
const SEARCH_KEYS = ['bmerke', 'bmodell', 'bfrom', 'bto', 'beier']

/**
 * Biler: search the fleet on the dealer's own theory ("10 year old HiAce
 * owners need a new van") and see WHICH ORGANISATIONS hold the matching cars.
 *
 * Modell is free text in the SVV data — the same car appears under many
 * strings — so the modell facet lists every variant with its count and the
 * user multi-selects. Nothing is collapsed client-side: the counts are the
 * honest signal of what a selection includes.
 */
export default function BilerTab({ page, onPageChange, markedsomradeIds, vehicleClass }: Props) {
  const { t } = useTranslation('leads')
  const PERIOD_HELP = t('biler.periodHelp')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // The applied search lives in the URL (#625 AC7): opening a bedrift
  // unmounts this tab, and "Tilbake til leads" returns to that URL — so the
  // search, the page and the result all come back, and refresh keeps them.
  // Null until the first search: the tab opens with the facet panel only.
  const searchKey = SEARCH_KEYS.flatMap(k => searchParams.getAll(k).map(v => `${k}=${v}`)).join('&')
  const appliedKey = `${searchKey}|${markedsomradeIds ?? ''}|${vehicleClass ?? ''}`
  const applied = useMemo<BilerSearchFilters | null>(() => {
    const s = bilerSearchFromParams(new URLSearchParams(searchKey))
    return s && { ...s, vehicle_class: vehicleClass, markedsomrade_ids: markedsomradeIds }
    // appliedKey covers every input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey])

  // Drafts are seeded from the applied search, so returning shows the inputs
  // the result was built from rather than empty inputs over a result.
  const [merker, setMerker] = useState<BilerMerkeFacet[]>([])
  const [merkerLoading, setMerkerLoading] = useState(true)
  const [merkeId, setMerkeId] = useState<number | ''>(() => applied?.merke_id ?? '')
  const [modeller, setModeller] = useState<BilerModellFacet[]>([])
  const [modellerLoading, setModellerLoading] = useState(false)
  const [selectedModeller, setSelectedModeller] = useState<Set<string>>(() => new Set(applied?.modell ?? []))
  const [modellFilter, setModellFilter] = useState('')
  const [regFrom, setRegFrom] = useState(() => applied?.reg_from ?? '')
  const [regTo, setRegTo] = useState(() => applied?.reg_to ?? '')
  const [eierskap, setEierskap] = useState<'' | 'eid' | 'leaset'>(() => applied?.eierskap ?? '')

  const [rows, setRows] = useState<BilerOrgRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Export (#869): every hit up to BILER_EXPORT_LIMIT, fetched on click from
  // the applied search, so edited-but-unsearched inputs never leak in.
  const kanEksportere = useKanEksportere()
  const [exportError, setExportError] = useState<string | null>(null)

  // Personal bookmarks (#335): the star column mirrors Mine Leads.
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()

  // The merke facet is slow — 10 s cold, and the markedsområde-scoped variant
  // is never cached (2 s every call), so the select sits there looking empty
  // for seconds. It has to say it is loading, and a superseded response must
  // never land on top of a newer one.
  // A scope or class switch invalidates the whole cascade — a merke may have
  // no cars in the new class, and the counts change either way — so it
  // clears the drafts and the applied search. Not on mount: the URL may carry
  // a search the user is returning to. The ref holds the previous scope, so
  // StrictMode's double effect run is not mistaken for a switch.
  const scopeKey = `${markedsomradeIds ?? ''}|${vehicleClass ?? ''}`
  const prevScopeKey = useRef(scopeKey)
  useEffect(() => {
    if (prevScopeKey.current === scopeKey) return
    prevScopeKey.current = scopeKey
    setMerkeId('')
    setSelectedModeller(new Set())
    setRegFrom('')
    setRegTo('')
    setEierskap('')
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const k of SEARCH_KEYS) next.delete(k)
      return next
    }, { replace: true })
  }, [scopeKey, setSearchParams])

  useLatest(alive => {
    setMerkerLoading(true)
    leadsAPI.getBilerMerker(markedsomradeIds, vehicleClass)
      .then(res => { if (alive()) setMerker(res.data.items) })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setMerkerLoading(false) })
  }, [markedsomradeIds, vehicleClass])

  // The period and eierskap narrow the modell list the way the merke does
  // (#625 AC2). An unfinished or inverted period is not sent — the facet
  // keeps its last list until the period is valid again.
  const regRangeInvalid = regFrom !== '' && regTo !== '' && regFrom > regTo
  const narrowFrom = regRangeInvalid ? '' : regFrom
  const narrowTo = regRangeInvalid ? '' : regTo
  useLatest(alive => {
    if (merkeId === '') { setModeller([]); return }
    setModellerLoading(true)
    leadsAPI.getBilerModeller(merkeId, markedsomradeIds, vehicleClass, {
      reg_from: narrowFrom || undefined, reg_to: narrowTo || undefined, eierskap: eierskap || undefined,
    })
      .then(res => {
        if (!alive()) return
        setModeller(res.data.items)
        // A ticked model the new list no longer has would be searched for
        // silently — drop it. Matched case-insensitively: the ci collation
        // folds "CRAFTER" and "Crafter" into one facet row whose label is
        // whichever spelling MySQL meets first, so a narrower list can name
        // the same model differently. Take the new label so the box stays ticked.
        const listed = new Map(res.data.items.map(m => [m.modell.toLowerCase(), m.modell]))
        setSelectedModeller(prev => {
          const kept = [...prev].flatMap(m => listed.get(m.toLowerCase()) ?? [])
          return kept.length === prev.size && kept.every(m => prev.has(m)) ? prev : new Set(kept)
        })
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setModellerLoading(false) })
  }, [merkeId, markedsomradeIds, vehicleClass, narrowFrom, narrowTo, eierskap])

  function handleMerkeChange(value: string) {
    setMerkeId(value === '' ? '' : Number(value))
    setSelectedModeller(new Set())
    setModellFilter('')
  }

  const visibleModeller = useMemo(() => {
    const f = modellFilter.trim().toLowerCase()
    return f ? modeller.filter(m => m.modell.toLowerCase().includes(f)) : modeller
  }, [modeller, modellFilter])

  const selectedCount = useMemo(
    () => modeller.filter(m => selectedModeller.has(m.modell))
      .reduce((n, m) => n + m.car_count, 0),
    [modeller, selectedModeller],
  )

  function toggleModell(modell: string) {
    setSelectedModeller(prev => {
      const next = new Set(prev)
      if (next.has(modell)) next.delete(modell)
      else next.add(modell)
      return next
    })
  }

  /** Write the search to the URL, which is what applies it. No cap on the
   *  number of models (#625 AC3): all 1,000 Mercedes-Benz facet strings make
   *  a ~19 KB request line, and the testing deployment was measured to accept
   *  64 KB (2026-09-23). */
  function handleSearch() {
    const search: BilerSearchFilters = {
      merke_id: merkeId === '' ? undefined : merkeId,
      modell: selectedModeller.size ? [...selectedModeller] : undefined,
      reg_from: regFrom || undefined,
      reg_to: regTo || undefined,
      eierskap: eierskap || undefined,
    }
    // One setSearchParams call: page and the search must land together, a
    // second call in the same tick would work from the stale location.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const k of SEARCH_KEYS) next.delete(k)
      next.delete('page')
      bilerSearchToParams(search, next)
      // always marks the URL as a Biler search, even with no filter at all
      if (![...next.keys()].some(k => SEARCH_KEYS.includes(k))) next.set('bmerke', '')
      return next
    }, { replace: true })
  }

  // Per-organisation merke/modell breakdown within the search (#625 AC6),
  // fetched when a row is expanded; reset with every new result page.
  const [expanded, setExpanded] = useState<Record<string, BilerModellBreakdown[] | 'loading' | 'error'>>({})
  const appliedKeyRef = useRef(appliedKey)
  appliedKeyRef.current = appliedKey

  useLatest(alive => {
    if (!applied) return
    setLoading(true)
    setError(null)
    setExportError(null)
    setExpanded({})
    leadsAPI.getBilerOrganisasjoner(applied, PAGE_SIZE, (page - 1) * PAGE_SIZE)
      .then(res => {
        if (!alive()) return
        setRows(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [appliedKey, page])

  // A response for a search the user has since replaced is dropped.
  function toggleExpanded(orgNr: string) {
    if (!applied) return
    if (expanded[orgNr]) {
      setExpanded(prev => { const next = { ...prev }; delete next[orgNr]; return next })
      return
    }
    setExpanded(prev => ({ ...prev, [orgNr]: 'loading' }))
    const forKey = appliedKey
    leadsAPI.getBilerModellfordeling(orgNr, applied)
      .then(res => {
        if (appliedKeyRef.current === forKey) setExpanded(prev => ({ ...prev, [orgNr]: res.data.items }))
      })
      .catch(() => {
        if (appliedKeyRef.current === forKey) setExpanded(prev => ({ ...prev, [orgNr]: 'error' }))
      })
  }

  /** Carry the applied search to the detail page in the URL so the Kjøretøy
   *  tab can sort the matching cars first — and so a refresh keeps them.
   *  location.search already holds the search, so Tilbake restores it. */
  function openOrg(orgNr: string) {
    const qs = applied ? bilerSearchToParams(applied) : new URLSearchParams()
    navigate(`/leads/bedrift/${orgNr}?${qs.toString()}`, {
      state: { from: location.pathname + location.search },
    })
  }

  const merkeNavn = applied?.merke_id !== undefined
    ? merker.find(m => m.merke_id === applied.merke_id)?.merke_navn ?? String(applied.merke_id)
    : null

  const summary = buildBilerSummary({
    total, totalIsCapped, merkeNavn,
    modell: applied?.modell,
    regFrom: applied?.reg_from,
    regTo: applied?.reg_to,
    eierskap: applied?.eierskap,
    vehicleClass: applied?.vehicle_class,
  })

  function loadExportRows() {
    if (!applied) return Promise.resolve([])
    setExportError(null)
    return leadsAPI.getBilerOrganisasjoner(applied, BILER_EXPORT_LIMIT, 0)
      .then(res => res.data.items)
      .catch(err => {
        setExportError(localizeApiDetail(err.response?.data?.detail) || err.message)
        throw err
      })
  }

  const regMonthValid = (v: string) => v === '' || /^\d{4}-(0[1-9]|1[0-2])$/.test(v)

  return (
    <div className="space-y-4">
      {/* Facet panel */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('common.merke')}</label>
            <select
              value={merkeId}
              onChange={e => handleMerkeChange(e.target.value)}
              disabled={merkerLoading}
              className="input-field text-sm min-w-[220px] disabled:opacity-60 disabled:cursor-wait"
            >
              <option value="">
                {merkerLoading
                  ? t('common.loadingMerker')
                  : merker.length === 0
                    ? t('biler.noMerker')
                    : t('biler.chooseMerke')}
              </option>
              {merker.map(m => (
                <option key={m.merke_id} value={m.merke_id}>
                  {m.merke_navn ?? m.merke_id} ({m.car_count.toLocaleString('nb-NO')})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="biler-reg-from" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('biler.regFrom')}
            </label>
            <input
              id="biler-reg-from"
              type="month"
              value={regFrom}
              onChange={e => setRegFrom(e.target.value)}
              title={PERIOD_HELP}
              className="input-field text-sm"
            />
          </div>
          <div>
            <label htmlFor="biler-reg-to" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('biler.regTo')}
            </label>
            <input
              id="biler-reg-to"
              type="month"
              value={regTo}
              onChange={e => setRegTo(e.target.value)}
              title={PERIOD_HELP}
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('biler.eierskap')}</label>
            <select
              value={eierskap}
              onChange={e => setEierskap(e.target.value as '' | 'eid' | 'leaset')}
              className="input-field text-sm"
            >
              <option value="">{t('common.all')}</option>
              <option value="eid">{t('biler.eid')}</option>
              <option value="leaset">{t('biler.leaset')}</option>
            </select>
          </div>
          <button
            onClick={handleSearch}
            disabled={regRangeInvalid || !regMonthValid(regFrom) || !regMonthValid(regTo)}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            {t('common.search')}
          </button>
          {regRangeInvalid && (
            <span className="text-xs text-red-600 dark:text-red-400">{t('common.rangeMonthInvalid')}</span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="biler-period-help">{PERIOD_HELP}</p>

        {/* Modell multi-select — every free-text variant with its count */}
        {merkeId !== '' && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('biler.modeller')} {selectedModeller.size > 0 && (
                  <span className="text-primary-600 dark:text-primary-400">
                    {t('biler.modellerSelected', { n: selectedModeller.size, cars: selectedCount.toLocaleString('nb-NO') })}
                  </span>
                )}
              </label>
              <input
                type="text"
                value={modellFilter}
                onChange={e => setModellFilter(e.target.value)}
                placeholder={t('biler.filterPlaceholder')}
                className="input-field text-xs py-1 w-48"
              />
              {visibleModeller.length > 0 && (
                <button
                  onClick={() => setSelectedModeller(new Set(visibleModeller.map(m => m.modell)))}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('biler.selectAllShown')}
                </button>
              )}
              {selectedModeller.size > 0 && (
                <button
                  onClick={() => setSelectedModeller(new Set())}
                  className="text-xs text-gray-500 hover:underline"
                >
                  {t('common.reset')}
                </button>
              )}
            </div>
            {modellerLoading ? (
              <p className="text-sm text-gray-500 py-2">{t('biler.loadingModeller')}</p>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-sm p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
                {visibleModeller.map(m => (
                  <label key={m.modell} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm px-1">
                    <input
                      type="checkbox"
                      checked={selectedModeller.has(m.modell)}
                      onChange={() => toggleModell(m.modell)}
                      className="rounded-sm"
                    />
                    <span className="truncate" title={m.modell}>{m.modell}</span>
                    <span className="text-xs text-gray-500 ml-auto">{m.car_count.toLocaleString('nb-NO')}</span>
                  </label>
                ))}
                {visibleModeller.length === 0 && (
                  <p className="text-sm text-gray-500 col-span-full py-1">{t('biler.noModellMatch')}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>}

      {/* Result: organisations ranked by matching cars */}
      {applied && !error && (
        <div className="card p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
              <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('common.searching')}
            </div>
          ) : (
            <>
              <p
                data-testid="biler-summary"
                className="mb-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200"
                title={(applied?.modell?.length ?? 0) > SUMMARY_MODELL_LIMIT ? applied?.modell?.join('\n') : undefined}
              >
                {summary}
              </p>
              {kanEksportere && rows.length > 0 && (
                <div className="flex items-center justify-end gap-3 flex-wrap mb-3" data-testid="biler-export">
                  {exportError && <span className="text-xs text-red-600 dark:text-red-400">{exportError}</span>}
                  {totalIsCapped && (
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="biler-export-cap">
                      {t('biler.exportCapNote', { n: BILER_EXPORT_LIMIT })}
                    </span>
                  )}
                  <ExportButtons
                    data={rows}
                    loadData={loadExportRows}
                    columns={bilerExportColumns(t)}
                    metadata={{ title: t('biler.exportTitle'), explanationText: summary }}
                  />
                </div>
              )}
              <table className="data-table data-table-compact w-full">
                <thead>
                  <tr>
                    <th className="w-8" aria-label={t('biler.showModels')} />
                    <th className="w-8" aria-label={t('common.mineLeads')} />
                    <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.orgNr')}</th>
                    <th className="text-right whitespace-nowrap">{t('biler.carsInHit')}</th>
                    <th className="text-right whitespace-nowrap">{t('biler.ofWhichLeased')}</th>
                    <th className="text-left whitespace-nowrap">{t('biler.oldest')}</th>
                    <th className="text-left whitespace-nowrap">{t('biler.newest')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.postnr')}</th>
                    <th className="text-center whitespace-nowrap">{t('common.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const breakdown = expanded[row.org_nr]
                    return (
                    <Fragment key={row.org_nr}>
                    <tr
                      className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}
                    >
                      <td className="text-center">
                        <button
                          onClick={() => toggleExpanded(row.org_nr)}
                          title={breakdown ? t('biler.hideModels') : t('biler.showModels')}
                          aria-label={breakdown ? t('biler.hideModels') : t('biler.showModels')}
                          aria-expanded={!!breakdown}
                          className="text-gray-500 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          {breakdown ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="text-center">
                        <BookmarkStar active={isBookmarked(row.org_nr)} onToggle={() => toggleBookmark(row.org_nr)} />
                      </td>
                      <td className="font-bold whitespace-nowrap">{row.navn ?? '—'}</td>
                      <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr}</td>
                      <td className="text-right font-bold">{row.car_count.toLocaleString('nb-NO')}</td>
                      <td className="text-right">{row.leased_count.toLocaleString('nb-NO')}</td>
                      <td className="text-sm">{row.oldest_reg ? formatNorwegianDate(row.oldest_reg) : '—'}</td>
                      <td className="text-sm">{row.newest_reg ? formatNorwegianDate(row.newest_reg) : '—'}</td>
                      <td className="font-mono text-xs">{row.postnummer ?? '—'}</td>
                      <td className="text-center">
                        <button
                          onClick={() => openOrg(row.org_nr)}
                          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <span className="text-xs font-medium">{t('common.see')}</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {breakdown && (
                      <tr data-testid={`biler-breakdown-${row.org_nr}`}>
                        <td />
                        <td colSpan={9} className="text-sm py-2">
                          {breakdown === 'loading' && <span className="text-gray-500">{t('biler.loadingModeller')}</span>}
                          {breakdown === 'error' && <span className="text-red-600 dark:text-red-400">{t('biler.breakdownError')}</span>}
                          {Array.isArray(breakdown) && (
                            <div className="flex flex-wrap gap-2">
                              {breakdown.map(b => (
                                <span
                                  key={`${b.merke}|${b.modell}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-primary-50 dark:bg-primary-900/20 text-gray-800 dark:text-gray-200"
                                >
                                  {b.merke ?? '—'} {b.modell ?? t('biler.noModell')}
                                  <span className="font-bold" data-testid="breakdown-count">{b.car_count.toLocaleString('nb-NO')}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    )
                  })}
                </tbody>
              </table>

              {rows.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('biler.noOrgs')}</p>
              )}

              <Pagination
                page={page}
                onPageChange={onPageChange}
                total={total}
                totalIsCapped={totalIsCapped}
                hasMore={hasMore}
                pageSize={PAGE_SIZE}
              />
            </>
          )}
        </div>
      )}

      {!applied && !error && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          {t('biler.prompt')}
        </p>
      )}
    </div>
  )
}
